import React, { useContext as useContextCore, useEffect } from 'react'
import type { CreateAndBuildContext, CreateAndBuildProviderProps } from '@ztechapps/webui.contract'
import { jsonTryParse } from '@ztechapps/common.utils'
import type { Product, ProductBillOfMaterial } from '@ztechapps/common.contract'
import { computeBomAsGreedy, useProductById } from '@ztechapps/product.common'
import { gql } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'

const singleProduct = gql`
  query singleProduct($filter: JSON) {
    singleProduct(filter: $filter) {
      _id
      fullBillOfMaterials
      code
      name
      hasSubComponents
      inventory {
        _id
        quantityOnHand
      }
      requiredComputedQuantityOnHand
      computedQuantityOnHand
      baseUom {
        _id
        symbol
      }
      unitOfMeasurePrecision
      workOrderUom {
        _id
        code
        symbol
      }
      workOrderConversionFactor
    }
  }
`

const context = React.createContext<CreateAndBuildContext>({} as CreateAndBuildContext)
const CoreProvider = context.Provider

export const CreateAndBuildProvider = ({
  children,
  productId,
  qtyToBuild: initialQtyToBuild,
  ...props
}: CreateAndBuildProviderProps) => {
  initialQtyToBuild = initialQtyToBuild ?? 1
  const productDocumentResult = useProductById({ _id: productId, query: singleProduct })

  const fullBillOfMaterials = productDocumentResult?.document?.fullBillOfMaterials
  const _id = productDocumentResult?.document?._id
  const [hasRequiredSubcomponents, setHasRequiredSubcomponents] = React.useState(false)
  const [updatedProduct, setUpdatedProduct] = React.useState<Product>(null)
  const [qtyToBuild, setQtyToBuild] = React.useState(initialQtyToBuild)

  useEffect(() => {
    const bom = jsonTryParse(fullBillOfMaterials) as ProductBillOfMaterial[]
    const computedProduct = computeBomAsGreedy(
      {
        ...productDocumentResult?.document,
        billOfMaterials: bom,
      },
      qtyToBuild,
      { requiredOnly: false }
    )

    setHasRequiredSubcomponents(!isEmpty(computedProduct?.billOfMaterials))
    setUpdatedProduct(computedProduct)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useProductDocument rebuilds document every render; listing it re-runs this setState effect without end
  }, [_id, fullBillOfMaterials, qtyToBuild])

  return (
    <CoreProvider
      value={{
        initialQtyToBuild: initialQtyToBuild,
        qtyToBuild,
        setQtyToBuild,
        product: updatedProduct,
        loading: productDocumentResult?.loading,
        productId,
        hasRequiredSubcomponents,
        ...props,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useCreateAndBuildContext = () => useContextCore(context)
